import { useMemo } from "react";
import { isEqual } from "lodash";

import { selectLayerByVersionAndLayerKey, selectVersionConfig } from "fond/api";
import { MapboxStyleLayer } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppSelector } from "fond/utils/hooks";

import { addFilterCondition, transformFilterForDrawMode, transformLayerConfigStylesForDrawing } from "../helper";

import { validationStyles } from "./styles";

/**
 * Type guard to check if a config entity is a LayerConfig with Styles property
 */
const isLayerConfig = (config: unknown): config is LayerConfig => {
  return !!config && typeof config === "object" && "Styles" in config && Array.isArray((config as LayerConfig).Styles);
};

/**
 * Type guard to check if a config entity has Styles (LayerConfig or SublayerConfig)
 */
const hasStyles = (config: unknown): config is { Styles: string[] } => {
  return !!config && typeof config === "object" && "Styles" in config && Array.isArray((config as { Styles: string[] }).Styles);
};

/**
 * Creates validation styles for a specific layer based on its geometry type.
 * Only includes validation styles that match the layer's geometry type.
 *
 * @param layerId - The layer ID to create validation styles for
 * @param geometryType - The geometry type of the layer (Point, LineString, or Polygon)
 * @returns Array of validation styles specific to this layer
 */
const createLayerValidationStyles = (layerId: string, geometryType: LayerConfig["GeometryType"]): MapboxStyleLayer[] => {
  const layerValidationStyles: MapboxStyleLayer[] = [];

  // Return early if geometry type is null or "Null" - no validation styles needed
  if (!geometryType || geometryType === "Null") {
    return layerValidationStyles;
  }

  // Map geometry types to their validation style prefixes
  // Multi-geometry types (MultiPoint, MultiLineString, MultiPolygon) use the same styles as their base types
  const geometryTypeToStylePrefix: Record<string, string[]> = {
    Point: ["gl-draw-point-invalid", "gl-draw-point-stroke-invalid"],
    MultiPoint: ["gl-draw-point-invalid", "gl-draw-point-stroke-invalid"],
    LineString: ["gl-draw-line-invalid"],
    MultiLineString: ["gl-draw-line-invalid"],
    Polygon: ["gl-draw-polygon-fill-invalid", "gl-draw-polygon-stroke-invalid"],
    MultiPolygon: ["gl-draw-polygon-fill-invalid", "gl-draw-polygon-stroke-invalid"],
  };

  const relevantStylePrefixes = geometryTypeToStylePrefix[geometryType];
  if (!relevantStylePrefixes) return layerValidationStyles;

  // Filter validation styles to only those matching this geometry type
  const relevantValidationStyles = validationStyles.filter((style) => relevantStylePrefixes.some((prefix) => style.id.startsWith(prefix)));

  // Create layer-specific copies of these validation styles
  relevantValidationStyles.forEach((style) => {
    // Remove $type from filter since we're creating geometry-specific styles
    let filterConditions: unknown[] = [];
    if (style.filter && Array.isArray(style.filter) && style.filter[0] === "all") {
      // Extract all conditions except $type check
      filterConditions = style.filter.slice(1).filter((condition) => {
        if (!Array.isArray(condition)) return true;
        return condition[0] !== "==" || condition[1] !== "$type";
      });
    }

    // Add layerId check to the filter (using shorthand syntax to match other conditions)
    filterConditions.push(["==", "user_layerId", layerId]);

    layerValidationStyles.push({
      ...style,
      id: `${layerId}-${style.id}`,
      filter: ["all", ...filterConditions],
    });
  });

  return layerValidationStyles;
};

/**
 * Helper to apply active state styling to a Mapbox layer style.
 * When a feature has `active: true`, it will be highlighted in the specified color.
 *
 * @param style - The MapboxStyle to apply active styling to
 * @param activeColor - The color to use for active features (default: "yellow")
 * @returns The paint object with active styling applied
 */
const applyActiveStateStyling = (style: Pick<MapboxStyleLayer, "type" | "paint">, activeColor: string = "yellow"): MapboxStyleLayer["paint"] => {
  const { type, paint } = style;

  if (!paint) return paint;

  switch (type) {
    case "symbol": {
      const symbolPaint = paint as Record<string, unknown>;
      return {
        ...paint,
        "text-color": ["case", ["==", ["get", "active"], "true"], activeColor, symbolPaint["text-color"] || "black"],
        "text-halo-color": ["case", ["==", ["get", "active"], "true"], activeColor, symbolPaint["text-halo-color"] || "black"],
      };
    }
    case "line": {
      const linePaint = paint as Record<string, unknown>;
      return {
        ...paint,
        "line-color": ["case", ["==", ["get", "active"], "true"], activeColor, linePaint["line-color"] || "black"],
      };
    }
    case "circle": {
      const circlePaint = paint as Record<string, unknown>;
      return {
        ...paint,
        "circle-color": ["case", ["==", ["get", "active"], "true"], activeColor, circlePaint["circle-color"] || "black"],
      };
    }
    default:
      return paint;
  }
};

/**
 * Custom hook to get drawing styles for multiple layers from the layer configuration.
 *
 * This hook processes all layer keys at once to comply with React's Rules of Hooks.
 * For layers with ShowSublayers enabled, it generates separate styles for each sublayer
 * to support independent visibility toggling.
 *
 * @param versionId - The version ID to get the layers from
 * @param layerKeys - Array of layer keys (e.g., ["input_layers/address", "input_layers/street"])
 * @returns Map of layerKey (or sublayerId) to MapboxStyleLayer array, or undefined if not available
 */
export const useMultiLayerDrawingStyles = (versionId: string, layerKeys: string[]): Map<string, MapboxStyleLayer[] | undefined> => {
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));

  // Get all layer data in one selector call
  const layers = useAppSelector((state) => {
    return layerKeys.map((layerKey) => {
      const layer = selectLayerByVersionAndLayerKey(state, { versionId, layerId: layerKey });
      return {
        layerKey,
        layer,
      };
    });
  }, isEqual);

  return useMemo(() => {
    const stylesMap = new Map<string, MapboxStyleLayer[] | undefined>();

    if (!config?.Data?.entities) {
      // Return empty map if we don't have the required data
      layerKeys.forEach((key) => stylesMap.set(key, undefined));
      return stylesMap;
    }

    // Process each layer
    layers.forEach(({ layerKey, layer }) => {
      const layerConfig = layer?.Configuration?.ID ? config.Data.entities[layer.Configuration.ID] : undefined;

      // If we don't have any layer config at all, skip this layer
      if (!layerConfig || typeof layerConfig !== "object") {
        stylesMap.set(layerKey, undefined);
        return;
      }

      // Process parent layer styles if it has them
      if (isLayerConfig(layerConfig)) {
        // Get base styles for the parent layer (no sublayer filters)
        const baseLayerStyles = layerConfig.Styles.map((styleId) => {
          const style = config.Data.entities[styleId];
          return style && "MapboxStyle" in style ? style : null;
        }).filter((style): style is NonNullable<typeof style> => style !== null);

        // Convert to Mapbox styles without sublayer filters
        const parentMapboxStyles = baseLayerStyles
          .map((layerStyle) => {
            if (!layerStyle.MapboxStyle) return null;

            const paint = applyActiveStateStyling(layerStyle.MapboxStyle);

            return {
              ...layerStyle.MapboxStyle,
              id: layerStyle.ID,
              paint,
            } as MapboxStyleLayer;
          })
          .filter((style): style is MapboxStyleLayer => style !== null);

        if (parentMapboxStyles.length > 0) {
          // Transform for use with drawing tools
          const transformed = transformLayerConfigStylesForDrawing(layerKey, parentMapboxStyles);

          // Create validation styles for parent layer
          const geometryType = layer?.Geometry;
          const validationStylesForLayer = geometryType ? createLayerValidationStyles(layerKey, geometryType) : [];

          // Combine and add to map
          const combinedStyles = [...validationStylesForLayer, ...transformed];
          stylesMap.set(layerKey, combinedStyles);
        } else {
          // Parent has no regular styles, but might have validation styles
          const geometryType = layer?.Geometry;
          const validationStylesForLayer = geometryType ? createLayerValidationStyles(layerKey, geometryType) : [];

          if (validationStylesForLayer.length > 0) {
            stylesMap.set(layerKey, validationStylesForLayer);
          } else {
            stylesMap.set(layerKey, undefined);
          }
        }
      } else {
        // Parent layer has no styles, but we still want to create validation styles if it has a geometry
        const geometryType = layer?.Geometry;
        const validationStylesForLayer = geometryType ? createLayerValidationStyles(layerKey, geometryType) : [];

        if (validationStylesForLayer.length > 0) {
          stylesMap.set(layerKey, validationStylesForLayer);
        } else {
          stylesMap.set(layerKey, undefined);
        }
      }

      // Process sublayers separately (if any)
      // This should happen regardless of whether parent has styles
      if ("Children" in layerConfig && layerConfig.Children) {
        layerConfig.Children.forEach((childId) => {
          const childConfig = config.Data.entities[childId];
          if (!hasStyles(childConfig)) return;

          const isSublayer = "Type" in childConfig && childConfig.Type === "SUBLAYER";
          if (!isSublayer) return;

          const sublayerConfig = childConfig as SublayerConfig;
          const sublayerFilter = sublayerConfig.FilterConfiguration?.Mapbox;
          if (!sublayerFilter) return;

          const transformedFilter = transformFilterForDrawMode(sublayerFilter);

          // Get sublayer styles
          const sublayerStyles = sublayerConfig.Styles.map((styleId) => {
            const style = config.Data.entities[styleId];
            return style && "MapboxStyle" in style ? style : null;
          }).filter((style): style is NonNullable<typeof style> => style !== null);

          // Convert to Mapbox styles with sublayer filter
          const sublayerMapboxStyles = sublayerStyles
            .map((layerStyle) => {
              if (!layerStyle.MapboxStyle) return null;

              const combinedFilter = addFilterCondition(layerStyle.MapboxStyle.filter, transformedFilter);
              const paint = applyActiveStateStyling(layerStyle.MapboxStyle);

              return {
                ...layerStyle.MapboxStyle,
                id: layerStyle.ID,
                filter: combinedFilter,
                paint,
              } as MapboxStyleLayer;
            })
            .filter((style): style is MapboxStyleLayer => style !== null);

          if (sublayerMapboxStyles.length > 0) {
            // Transform for use with drawing tools
            // Important: Use parent layer key for user_layerId filter since features belong to parent layer
            // but use sublayer ID as prefix for style IDs to enable independent visibility control
            const transformedWithParentFilter = sublayerMapboxStyles.map((style) => ({
              ...style,
              id: `${childId}-${style.id}`,
              filter: addFilterCondition(style.filter, ["==", ["get", "user_layerId"], layerKey]),
            }));

            // Create validation styles for sublayer using sublayer ID as prefix
            const geometryType = layer?.Geometry;
            const validationStylesForSublayer = geometryType ? createLayerValidationStyles(childId, geometryType) : [];

            // Update validation style filters to include sublayer filter AND parent layer ID filter
            const filteredValidationStyles = validationStylesForSublayer.map((style) => ({
              ...style,
              filter: addFilterCondition(addFilterCondition(style.filter, transformedFilter), ["==", ["get", "user_layerId"], layerKey]),
            }));

            // Combine and add to map with sublayer ID as key
            const combinedStyles = [...filteredValidationStyles, ...transformedWithParentFilter];
            stylesMap.set(childId, combinedStyles);
          }
        });
      }
    });

    return stylesMap;
  }, [layers, layerKeys, config]);
};
